import React, { useState, useRef } from 'react';
import { Clipboard, Check, AlertCircle, Loader, FileText, Search } from 'lucide-react';
import { useTemplates, useReservationList, useReservationContext, templateApi } from '../../../hooks/useTemplates';
import './QuickRenderPage.css';

function QuickRenderPage() {
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedReservationId, setSelectedReservationId] = useState('');
  const [reservationSearch, setReservationSearch] = useState('');
  const [rendered, setRendered] = useState(null);
  const [error, setError] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const iframeRef = useRef(null);

  // Load ALL templates (not just active) so the dropdown is never empty
  const { templates, isLoading: templatesLoading, error: templatesError } = useTemplates();
  const { reservations, isLoading: reservationsLoading } = useReservationList();
  const { context, isLoading: contextLoading, error: contextError } = useReservationContext(selectedReservationId);

  const filteredReservations = reservations.filter((r) => {
    if (!reservationSearch) return true;
    const search = reservationSearch.toLowerCase();
    return (
      (r.reservation_number || '').toLowerCase().includes(search) ||
      (r.email || '').toLowerCase().includes(search) ||
      (r.status || '').toLowerCase().includes(search)
    );
  });

  const handleRender = async () => {
    if (!selectedTemplateId || !context) return;

    setIsRendering(true);
    setError(null);
    setRendered(null);

    try {
      const result = await templateApi.preview(selectedTemplateId, context);
      setRendered(result.data);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Rendering failed';
      setError(msg);
    } finally {
      setIsRendering(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const copyHtmlBody = () => {
    if (rendered?.htmlBody) {
      copyToClipboard(rendered.htmlBody, 'body');
    }
  };

  const copyRichText = async () => {
    if (!rendered?.htmlBody) return;
    try {
      // Extract just the inner body content, stripping the full document wrapper
      // Outlook doesn't handle <!doctype>, <html>, <head>, <body> well when pasting
      const parser = new DOMParser();
      const doc = parser.parseFromString(rendered.htmlBody, 'text/html');
      const bodyHtml = doc.body.innerHTML;

      const blob = new Blob([bodyHtml], { type: 'text/html' });
      const item = new ClipboardItem({ 'text/html': blob });
      await navigator.clipboard.write([item]);
      setCopiedField('rich');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Fallback: select the iframe content and copy
      try {
        const iframe = iframeRef.current;
        const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
        if (iframeDoc) {
          const range = iframeDoc.createRange();
          range.selectNodeContents(iframeDoc.body);
          const sel = iframeDoc.defaultView.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
          iframeDoc.execCommand('copy');
          sel.removeAllRanges();
        }
        setCopiedField('rich');
        setTimeout(() => setCopiedField(null), 2000);
      } catch {
        // Last resort: copy as plain text
        copyToClipboard(rendered.htmlBody, 'rich');
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const selectedReservation = reservations.find((r) => String(r.id) === String(selectedReservationId));
  const canRender = selectedTemplateId && selectedReservationId && context && !contextLoading;

  return (
    <div className="quick-render">
      <div className="email-page-header">
        <h3>Quick Render</h3>
        <span className="quick-render-subtitle">
          Render a template with real reservation data, then copy and paste into your email
        </span>
      </div>

      {/* Show loading/error state for data fetching */}
      {templatesError && (
        <div className="quick-render-error">
          <AlertCircle size={16} />
          <span>Failed to load templates: {templatesError.response?.data?.error || templatesError.message}</span>
        </div>
      )}
      {contextError && (
        <div className="quick-render-error">
          <AlertCircle size={16} />
          <span>Failed to load reservation data: {contextError.response?.data?.error || contextError.message}</span>
        </div>
      )}

      <div className="quick-render-selectors">
        {/* Template Selector */}
        <div className="quick-render-selector">
          <label>Template</label>
          {templatesLoading ? (
            <div className="quick-render-loading"><Loader size={14} className="template-preview-spinner" /> Loading templates...</div>
          ) : (
            <select
              className="email-form-input"
              value={selectedTemplateId}
              onChange={(e) => {
                setSelectedTemplateId(e.target.value);
                setRendered(null);
                setError(null);
              }}
            >
              <option value="">Select a template...</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.category}){!t.is_active ? ' [inactive]' : ''}
                </option>
              ))}
            </select>
          )}
          {!templatesLoading && templates.length === 0 && !templatesError && (
            <div className="quick-render-meta">No templates found. Create one in the Templates tab first.</div>
          )}
          {selectedTemplate && (
            <div className="quick-render-meta">
              Subject: <code>{selectedTemplate.subject_tpl}</code>
            </div>
          )}
        </div>

        {/* Reservation Selector */}
        <div className="quick-render-selector">
          <label>Reservation</label>
          {reservationsLoading ? (
            <div className="quick-render-loading"><Loader size={14} className="template-preview-spinner" /> Loading reservations...</div>
          ) : (
            <>
              <div className="quick-render-search-wrapper">
                <Search size={14} className="quick-render-search-icon" />
                <input
                  type="text"
                  className="email-form-input quick-render-search-input"
                  placeholder="Search by number, email, or status..."
                  value={reservationSearch}
                  onChange={(e) => setReservationSearch(e.target.value)}
                />
              </div>
              <select
                className="email-form-input"
                value={selectedReservationId}
                onChange={(e) => {
                  setSelectedReservationId(e.target.value);
                  setRendered(null);
                  setError(null);
                }}
                size={Math.min(filteredReservations.length + 1, 8)}
              >
                <option value="">Select a reservation...</option>
                {filteredReservations.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.reservation_number || `#${r.id}`} — {r.email} ({formatDate(r.check_in_date)} – {formatDate(r.check_out_date)}) [{r.status}]
                  </option>
                ))}
              </select>
            </>
          )}
          {!reservationsLoading && reservations.length === 0 && (
            <div className="quick-render-meta">No reservations found.</div>
          )}
          {contextLoading && selectedReservationId && (
            <div className="quick-render-loading"><Loader size={14} className="template-preview-spinner" /> Loading reservation data...</div>
          )}
          {selectedReservation && context && (
            <div className="quick-render-meta">
              {context.customer.firstName} {context.customer.lastName} — {context.reservation.nights} night(s), {context.reservation.status}
            </div>
          )}
        </div>
      </div>

      {/* Render Button */}
      <div className="quick-render-actions">
        <button
          className="btn btn-primary"
          onClick={handleRender}
          disabled={!canRender || isRendering}
        >
          {isRendering ? (
            <><Loader size={14} className="template-preview-spinner" /> Rendering...</>
          ) : (
            <><FileText size={14} /> Render Template</>
          )}
        </button>
        {!canRender && selectedTemplateId && selectedReservationId && contextLoading && (
          <span className="quick-render-hint">Loading reservation context...</span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="quick-render-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Rendered Output */}
      {rendered && (
        <div className="quick-render-output">
          {/* Subject */}
          <div className="quick-render-output-section">
            <div className="quick-render-output-header">
              <label>Subject</label>
              <button
                className="quick-render-copy-btn"
                onClick={() => copyToClipboard(rendered.subject, 'subject')}
              >
                {copiedField === 'subject' ? <><Check size={14} /> Copied</> : <><Clipboard size={14} /> Copy</>}
              </button>
            </div>
            <div className="quick-render-subject-text">{rendered.subject}</div>
          </div>

          {/* HTML Body */}
          <div className="quick-render-output-section">
            <div className="quick-render-output-header">
              <label>Email Body</label>
              <div className="quick-render-copy-btns">
                <button
                  className="quick-render-copy-btn"
                  onClick={copyRichText}
                >
                  {copiedField === 'rich' ? <><Check size={14} /> Copied</> : <><Clipboard size={14} /> Copy for Outlook</>}
                </button>
                <button
                  className="quick-render-copy-btn"
                  onClick={copyHtmlBody}
                >
                  {copiedField === 'body' ? <><Check size={14} /> Copied</> : <><Clipboard size={14} /> Copy HTML</>}
                </button>
              </div>
            </div>
            <div className="quick-render-body-preview">
              <iframe
                ref={iframeRef}
                title="Rendered Email"
                srcDoc={rendered.htmlBody}
                sandbox=""
                className="quick-render-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuickRenderPage;
